import { PkCard, PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { MouseEvent } from 'react';
import { memo, useMemo } from 'react';

import { useVideoPlaylistStore } from '../../store';

import type { EditorialContent } from '@cobalt/utilities';
import { useWindowSize } from '@hooks/useWindowSize';
import { getVideoUrl } from '@services/LinkRuleService';
import Image from '@shared/Image';
import { useTranslateFn } from '@shared/Translate';
import VideoJsonLd from '@shared/VideoPlayer/VideoJsonLd';
import { IS_BAD_NETWORK_DETECTED } from '@utilities/browser';

import * as classes from './styles.module.scss';

dayjs.extend(relativeTime);

const size = IS_BAD_NETWORK_DETECTED ? '4' : '1';
const fallbackImg = `${window.imgBaseUrl}/imgml/uefacom/${window.competitionFolder}/fallback.jpg`;

const VideoCard = ({ video }: { video: EditorialContent }) => {
  const { isDesktop, isLandscape } = useWindowSize();
  const t = useTranslateFn();

  const selectedVideo = useVideoPlaylistStore((state) => state.selectedVideo);
  const setSelected = useVideoPlaylistStore((state) => state.setSelected);

  const selected = useMemo(() => selectedVideo?.id === video.id, [selectedVideo?.id, video.id]);
  const timeAgo = useMemo(() => {
    const d = dayjs(video.publicationDate);
    if (dayjs().diff(d, 'day') > 14) {
      return d.format('DD/MM/YYYY');
    }

    return d.fromNow();
  }, [video.publicationDate]);

  const duration = useMemo(() => {
    if (!video?.duration) return '';

    if (video.duration >= 3600) {
      return dayjs.duration(video.duration, 's').format('HH:mm:ss');
    }

    return dayjs.duration(video.duration, 's').format('mm:ss');
  }, [video?.duration]);

  const thumbnail = useMemo(() => {
    const src = video?.mainMedia?.image?.wide?.[`w${size}`] ?? video?.imageUrl;
    return src || fallbackImg;
  }, [video]);

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    setSelected(video);
  };

  return (
    <a
      className={clsx(classes.container, selected && classes.selected)}
      role='option'
      aria-selected={selected}
      href={getVideoUrl(video.id)}
      onClick={handleClick}
    >
      <VideoJsonLd video={video} />
      {selected && (
        <div className={classes.overlay}>
          <PkIcon color='interaction-high-contrast' className='pk-mr--xs2' name='av-play' size={20} />
          <div className={classes.nowPlaying}>{t('now_playing')}</div>
        </div>
      )}
      <PkCard
        className={classes.card}
        content={isDesktop || isLandscape ? 'over' : 'default'}
        orientation={isDesktop || isLandscape ? 'portrait' : 'horizontal'}
        stroke={!(isDesktop || isLandscape)}
      >
        <div className={classes.heading} slot='heading'>
          <div className={classes.iconPlay}>
            {!!duration && <span className={classes.duration}>{duration}</span>}
            <PkIcon name='av-play-circle' color='interaction-high-contrast' />
          </div>
          <Image className={classes.thumbnail} title={video.title} src={thumbnail} fallback={fallbackImg} />
        </div>
        <div slot='body'>
          <div className={classes.date}>{timeAgo}</div>
          <div className={classes.title}>{video.title}</div>
        </div>
      </PkCard>
    </a>
  );
};

export default memo(VideoCard);
